<template>
  <div class="component-wrapper">
    <div class="xpage-top">
      <div class="section-left xpage-top-col1">
        <div class="xpage-top-profile">
          <h2
            class="applicant-name fullname-wrap text-overflow-ellipsis"
            style="display: block !important"
            :title="summary.primaryApplicantFullName"
            v-title-overflow
          >
            {{ summary.primaryApplicantFullName }}
          </h2>
          <h3 class="lender-name" v-title-overflow :title="summary.lenderName">
            {{ summary.lenderName }}
          </h3>
          <div class="status-container">
            <div class="application-actions">
              <PermissionsWrapper
                :showDisabledIfNotVisible="true"
                :permissionIds="[Permissions.ProgramConfiguration]"
                :permissionLevel="PermissionLevels.Update"
              >
                <application-status />
              </PermissionsWrapper>
              <PermissionsButton
                v-if="summary.isRightToCancel"
                :permissionIds="[Permissions.RightToCancel]"
                :permissionLevel="PermissionLevels.Update"
                @click="showCancelModal = true"
                class="cancel-application-btn"
              >
                Cancel Application
              </PermissionsButton>
              <div
                class="xpage-top-details-status"
                v-auto-animate="{ ...$animate, duration: 100 }"
              >
                <button
                  v-if="showMoveToLoanService"
                  type="submit"
                  @click="moveToLoanService(true)"
                  class="loan-service-btn"
                >
                  <span v-if="showMoveToLoanService"
                    >Move To Loan Service »</span
                  >
                  <span v-else>Processing...</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="section-right">
        <div class="xpage-top-row" id="middle">
          <div class="xpage-top-column">
            <div class="xpage-top-col2">
              <div class="loan-data">
                <div class="loan-info">
                  <h3 class="summary-top-value">Loan Amount</h3>
                  <h3 class="summary-mid-value">
                    {{ summary.loanAmountDisplay }}
                  </h3>
                  <h4 class="summary-low-value">
                    Max Approved:
                    {{ applicationStore.summary.maxApprovedLimitDisplay }}
                  </h4>
                </div>
              </div>
            </div>
          </div>
          <div class="xpage-top-column">
            <div class="xpage-top-col3">
              <div class="loan-data">
                <div class="loan-info">
                  <h3 class="summary-top-value">Interest Rate</h3>
                  <h3 class="summary-mid-value">{{ summary.rate }}</h3>
                  <h4 class="summary-low-value">{{ summary.rateTypeName }}</h4>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="xpage-top-row" id="stepper">
          <div class="xpage-top-right">
            <div class="stepper">
              <div
                v-for="(item, index) in stats"
                :key="item.name"
                :class="['step', getStepClass(index)]"
              >
                <div class="step-icons">
                  <CheckCircleIcon
                    class="step-icon"
                    v-if="getStepClass(index) === 'step-complete'"
                  />
                  <QuestionMarkCircleIcon
                    class="step-icon"
                    v-else-if="getStepClass(index) === 'step-active'"
                  />
                  <ExclamationCircleIcon class="step-icon" v-else />
                </div>
                <span class="step-title" v-html="item.name"></span>
                <div class="step-content">
                  <div class="stat">{{ item.date }}</div>
                </div>
                <div v-if="index < stats.length - 1" class="step-arrow">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="arrow-icon"
                    :class="['arrow-na', { 'arrow-na': item.date === 'N/A' }]"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill="currentColor"
                      d="M11 16H3V8h8V2l10 10l-10 10zm2-9v3H5v4h8v3l5-5z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- <div class="xcollapse-mobile">
        <a
          href="#"
          :class="toggleIconClass"
          @click.prevent="toggleApplicationDetailsMobile"
        ></a>
      </div> -->

      <CancellationModal
        v-if="showCancelModal"
        @update="updateApplication"
        @close="showCancelModal = $event"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watchEffect } from "vue";
import { storeToRefs } from "pinia";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import { Permissions, PermissionLevels } from "@/models/permissions";
import PermissionsWrapper from "@/components/wrappers/PermissionsWrapper.vue";
import PermissionsButton from "@/components/wrappers/PermissionsButton.vue";
import ApplicationStatus from "@/components/v2/application/ApplicationStatus.vue";
import CancellationModal from "@/components/v2/application/refi/CancellationModal.vue";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/vue/24/solid";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { usePayoffTabStore } from "@/store/store/payoffTabStore";
import { useApplicationDetails } from "@/composables/application/useApplicationDetails";

interface StatItem {
  name: string;
  status?: string;
  date?: string;
  step?: string | number;
}

interface ErrorResponseData {
  message?: string;
  errors?: string[];
}

const applicationStore = useApplicationStore();
const payoffTabStore = usePayoffTabStore();

const router = useRouter();
const { summary } = storeToRefs(applicationStore);

const currentStepIndex = ref(-1);
const showCancelModal = ref(false);
const mobileHidden = ref(true);
const store = useStore();

const rightToCancel = applicationStore.viewModel.rightToCancelDisplay;

const submittedDisplay = applicationStore.viewModel.submittedDisplay;

const esignEnvelopeCreatedDisplay =
  applicationStore.viewModel.esignEnvelopeCreatedDisplay;

const esignEnvelopeSignedDisplay =
  applicationStore.viewModel.esignEnvelopeSignedDisplay;

const anticipatedDisbursementDate =
  applicationStore.viewModel.anticipatedDibursementDisplay;

const stats: StatItem[] = [
  { name: "Application<br>Submitted", date: submittedDisplay },
  { name: "E-Sign Envelope<br>Created", date: esignEnvelopeCreatedDisplay },
  { name: "E-Sign Envelope<br>Signed", date: esignEnvelopeSignedDisplay },
  { name: "Right to Cancel<br>End Date", date: rightToCancel },
  {
    name: "Anticipated<br>Disbursement Date",
    date: anticipatedDisbursementDate,
  },
];

const { moveToLoanService } = useApplicationDetails();

const showMoveToLoanService = computed(() => {
  return (
    applicationStore.viewModel.statusName === "Complete" &&
    !applicationStore.viewModel.loanId
  );
});

const toggleIconClass = computed(() =>
  mobileHidden.value ? "icon-down-open-big" : "icon-up-open-big"
);

const toggleApplicationDetailsMobile = () => {
  mobileHidden.value = !mobileHidden.value;
};

const updateApplication = async () => {
  const referenceId = applicationStore.referenceId;
  await Promise.all([
    applicationStore.getApplication(referenceId),
    applicationStore.getApplicationSummary(referenceId),
    applicationStore.getApplicationComments(referenceId),
  ]);
};

function getStepClass(index: number): string {
  const RTC_STEP_INDEX = 3;
  const DISBURSEMENT_STEP_INDEX = 4;

  const isRTCDatePassed = () => {
    const rtcDate = stats[RTC_STEP_INDEX].date;
    if (!rtcDate || rtcDate === "N/A") return false;
    return new Date(rtcDate) < new Date();
  };

  if (currentStepIndex.value === -1) {
    for (let i = 0; i < stats.length; i++) {
      if (!stats[i].date || stats[i].date === "N/A") {
        currentStepIndex.value = i;
        break;
      }
    }
    if (currentStepIndex.value === -1) {
      currentStepIndex.value = stats.length - 1;
    }
  }

  if (index === RTC_STEP_INDEX) {
    if (stats[index].date && stats[index].date !== "N/A") {
      return isRTCDatePassed() ? "step-complete" : "step-active";
    }
  }

  if (index === DISBURSEMENT_STEP_INDEX) {
    if (stats[index].date && stats[index].date !== "N/A" && isRTCDatePassed()) {
      return "step-complete";
    }
    return "step-na";
  }

  if (
    index < RTC_STEP_INDEX &&
    stats[index].date &&
    stats[index].date !== "N/A"
  ) {
    return "step-complete";
  }

  if (index === currentStepIndex.value) {
    return "step-active";
  }

  return "step-na";
}

onMounted(() => {
  for (let i = 0; i < stats.length; i++) {
    if (!stats[i].date || stats[i].date === "N/A") {
      currentStepIndex.value = i;
      break;
    }
  }
  if (currentStepIndex.value === -1) {
    currentStepIndex.value = stats.length - 1;
  }
});
</script>

<style scoped>
.applicant-name {
  margin-bottom: 5px;
  font-size: 35px;
}

.applicant-name h2 {
  display: block !important;
}

.application-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 5px;
  position: relative;
  border-radius: 5px;
  z-index: 1;
}

.arrow-icon {
  width: 20px;
  height: 20px;
  margin-top: 0.1rem;
}

.cancel-application-btn,
.loan-service-btn {
  padding: 0 12px;
  font-weight: 600;
  border: none;
  line-height: 26px;
  cursor: pointer;
  border-radius: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.cancel-application-btn {
  margin-top: 5px;
  background-color: black;
  color: white;
}

.cancel-modal {
  background-color: white;
  padding: 20px;
  border-radius: 5px;
  width: 640px;
  height: 460px;
  text-align: center;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.2);
}

.component-wrapper {
  width: 100%;
  background-color: #e2e4e9;
  padding-left: 20px;
  padding-right: 0px;
  padding-top: 0;
  padding-bottom: 0;
}

div.xpage-top-right {
  padding: 0;
  white-space: wrap;
}

div .xpage-top {
  padding: 10px 10px;
}

div {
  font-family: "Montserrat", arial, sans-serif;
}

.lender-name {
  margin-bottom: 5px;
  font-size: 14px;
  font-weight: 700;
}

.loan-data {
  margin-bottom: 10px;
}

.loan-info h3 {
  font-size: 20px;
  margin: 5px 0;
}

.loan-info h4 {
  font-size: 14px;
  margin: 5px 0;
}

.loan-service-btn {
  background-color: #48a842;
  color: white;
  min-width: 160px;
}

.loan-service-btn:hover {
  background: #0ecb8e;
}

.status-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.stepper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
  max-width: 1431px;
  background-color: #ffffff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
  padding: 10px;
  position: relative;
}

.step {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 0.2rem;
  transition: color 0.3s ease;
  position: relative;
}

.step-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: -10px;
  z-index: 1;
}

.step-icons {
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-icon {
  width: 28px;
  height: 28px;
}

.step-active {
  color: #f7941d;
}

.step-complete {
  color: #48a842;
}

.step-na {
  color: #e0e0e0;
}

.arrow-na {
  fill: #e0e0e0;
}

.step-title {
  font-size: 14px;
  font-weight: 700;
  margin-top: 5px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-all;
  text-align: center;
  max-width: 100%;
}

.step-content {
  font-size: 0.75rem;
}

.stat {
  font-size: 18px;
  margin-top: 5px;
  font-weight: 700;
  margin-bottom: 5px;
}

.summary-mid-value {
  font-size: 30px;
  margin-bottom: 10px;
  padding-top: 5px;
}

.summary-low-value {
  font-size: 14px;
  margin-bottom: 10px;
  padding-top: 5px;
}

.summary-top-value {
  font-size: 14px;
  font-weight: 700;
  margin: 5px 0;
  padding-top: 5px;
}

/* Parent Container */
.xpage-top {
  display: flex;
  align-items: flex-start;
  gap: 20px;
}

/* Column 1 (15%) */
.xpage-top-col1 {
  width: 25%;
  display: flex;
  flex-direction: column;
}

/* Column 2 (15%) */
.xpage-top-col2 {
  display: flex;
  flex-direction: column;
}

/* Column 3 (15%) */
.xpage-top-col3 {
  display: flex;
  flex-direction: column;
}

/* Column 4 (Stepper, 55%) */
.xpage-top-right {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.xpage-top-details-status {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.xpage-top-row#stepper {
  display: flex;
}

.section-left {
  display: flex;
  gap: 6px;
}

.section-right {
  display: flex;
  gap: 22px;
  width: 75%;
}

.xpage-top-row#stepper {
  width: 75%;
}

.xpage-top-row#middle {
  display: flex;
  gap: 22px;
  width: 25%;
}

@media screen and (max-width: 1279px) {
  .xpage-top-profile {
    margin-right: 0;
  }
  .xpage-top-col1 {
    width: 40%;
  }
  div.stepper {
    min-width: 824px;
  }

  div.xpage-top-right {
    overflow: auto;
    border: transparent;
    border-radius: 6px;
    margin-top: 0;
  }

  .xpage-top-col2,
  .xpage-top-col3 {
    min-width: 155px;
  }

  .section-right {
    display: flex;
    flex-direction: column;
    width: calc(100% - 22px - 40%);
  }
  .xpage-top-row#stepper {
    display: flex;
    width: 100%;
  }
}

@media screen and (max-width: 992px) {
  div.xpage-top-profile {
    margin-top: 0;
  }
  div.xpage-top-profile {
    margin: 0;
  }

  div.xpage-top-col1 {
    width: 100%;
  }

  div .xpage-top {
    flex-wrap: wrap;
  }
}
@media screen and (max-width: 768px) {
  .xpage-top {
    flex-direction: column;
    align-items: center;
  }
  .xcollapse-mobile {
    display: none;
  }
  .status-container {
    align-items: center;
  }
  .component-wrapper {
    padding: 0;
  }

  .xpage-top-col1,
  .xpage-top-col2,
  .xpage-top-col3,
  .xpage-top-right {
    width: 100%;
  }

  .xpage-top-col1,
  .xpage-top-col2,
  .xpage-top-col3 {
    text-align: center;
  }

  .application-actions {
    flex-direction: column;
    align-items: flex-start;
  }
  .cancel-application-btn,
  .loan-service-btn {
    width: 100%;
  }
}
</style>
